import Tesseract from 'tesseract.js';

const ORDER_KEYWORDS = [
  'order', 'ship to', 'ship by', 'ordered', 'shipping', 'etsy', 'website',
  'order number', 'customer', 'delivery', 'parcel', 'tracking', 'vat', 'total'
];

function scoreOrderDetailsLikelihood(text) {
  if (!text || typeof text !== 'string') return 0;
  const lower = text.toLowerCase();
  return ORDER_KEYWORDS.filter((kw) => lower.includes(kw)).length;
}

function parseOrderFields(text) {
  const result = {
    customerName: '',
    orderNumber: '',
    orderDate: null,
    targetDate: null,
    sourceOfOrder: null,
    modeOfParcel: '',
  };
  if (!text || typeof text !== 'string') return result;

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const full = text.replace(/\s+/g, ' ');

  // Order number: # or £ (OCR often reads # as £), 5+ digits
  const orderNumMatch = text.match(/[#£]\s*(\d{5,})/i) || full.match(/(?:order\s*[#£]?|order\s*number)\s*[:\s]*(\d{5,})/i) || full.match(/[#£](\d{8,})/i);
  if (orderNumMatch) result.orderNumber = orderNumMatch[1].trim();

  // Dates: multiple formats
  function parseDateStr(s) {
    if (!s) return null;
    const t = String(s).trim();
    const d = new Date(t);
    if (!isNaN(d.getTime())) return d;
    const slash = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
    const dash = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dash) return new Date(Number(dash[1]), Number(dash[2]) - 1, Number(dash[3]));
    return null;
  }
  const monthNames = 'january|february|march|april|may|june|july|august|september|october|november|december';
  const orderedMatch = full.match(/(?:ordered|order\s*date)\s*[:\s]*([a-z]+\s+\d{1,2},?\s*\d{4})/i)
    || full.match(/(?:ordered|order\s*date)\s*[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})/i)
    || full.match(/(?:ordered|order\s*date)\s*[:\s]*(\d{4}-\d{2}-\d{2})/i)
    || full.match(new RegExp(`(\\d{1,2})\\s+(${monthNames})\\s+(\\d{4})`, 'i'))
    || full.match(new RegExp(`(${monthNames})\\s+(\\d{1,2}),?\\s+(\\d{4})`, 'i'));
  if (orderedMatch && orderedMatch[2] && orderedMatch[3]) {
    const monthMap = { january:0,february:1,march:2,april:3,may:4,june:5,july:6,august:7,september:8,october:9,november:10,december:11 };
    const m = (orderedMatch[2] || '').toLowerCase();
    if (monthMap[m] !== undefined) {
      const day = parseInt(orderedMatch[1] || orderedMatch[2], 10);
      const year = parseInt(orderedMatch[3], 10);
      result.orderDate = new Date(year, monthMap[m], day);
    }
  } else if (orderedMatch && orderedMatch[1]) {
    result.orderDate = parseDateStr(orderedMatch[1]);
  }
  const shipByMatch = full.match(/(?:ship\s*by|target|delivery)\s*[:\s]*([a-z]+\s+\d{1,2},?\s*\d{4})/i)
    || full.match(/(?:ship\s*by|target|delivery)\s*[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})/i)
    || full.match(/(?:ship\s*by|target|delivery)\s*[:\s]*(\d{4}-\d{2}-\d{2})/i);
  if (shipByMatch) result.targetDate = parseDateStr(shipByMatch[1]);

  // Customer name: reject table metadata and address lines (layout: "Ship to" then name, then address).
  function isRejectedAsName(s) {
    const t = (s || '').trim().toLowerCase();
    if (!t) return true;
    if (/^quantity\s*\d*$|^quantity\s+\d+$|^sku\s|^price\s|^amount\s|^\d+\s*$/.test(t)) return true;
    if (/^(quantity|sku|price|amount|item|product)\s/i.test(t) && /\d/.test(t)) return true;
    if (/^[\d\s\$\.\,]+$/.test(t)) return true;
    if (/^(japan|usa|austria|germany|france|india|china|united\s*states)$/i.test(t)) return true;
    if (/\bbldg\.?\b|building\b/i.test(t)) return true;
    if (/^〒|^\d{3,}\s*-?\s*\d/.test(t)) return true;
    if (/\bcity\b|\bprefecture\b|sumiyoshi\s*\d|^\d+-\d+-\d+/.test(t)) return true;
    if (/^copy\s*address$|copy\s*address/i.test(t)) return true;
    if (/\bstandard\s*shipping\b|\bgeneral\s*profile\b|\bexpress\s*shipping\b/i.test(t)) return true;
    if (/^customer(\s*[x×]|\s*$)/i.test(t) || /^customer$/i.test(t)) return true;
    if (/^[x×]$/i.test(t)) return true;
    if (/\$|\d+\.?\d*\s*\$/.test(t)) return true;
    return false;
  }
  function cleanNameCandidates(s) {
    if (!s || typeof s !== 'string') return [];
    let t = s.trim().replace(/\s+/g, ' ');
    t = t.replace(/[\^▾▼◆●\s]+$/g, '').trim();
    const out = [t];
    const stripped = t.replace(/[^\wäöüß\s.\-']/gi, ' ').replace(/\s+/g, ' ').trim();
    if (stripped && stripped !== t) out.push(stripped);
    return out;
  }
  function looksLikeName(s, allowSingleWord = false) {
    if (!s || s.length > 60) return false;
    if (isRejectedAsName(s)) return false;
    const t = s.trim().replace(/\s+/g, ' ');
    if (/^\d|^\+\d|\d{5,}|@|\.com|\.at\b|straße|street|st\.|road|rd\.|avenue|ave\./i.test(t)) return false;
    const words = t.split(/\s+/).filter(Boolean);
    if (words.length < 1 || words.length > 5) return false;
    if (!allowSingleWord && words.length < 2) return false;
    return words.every((w) => /^[\wäöüß.\-']+$/i.test(w) && w.length > 0);
  }
  const sameLineMatch = full.match(/(?:ship\s*to|customer|recipient|delivery\s*to|send\s*to|shipping\s*address)[:\s\^]*([a-zäöüß\s.\-']{2,60}?)(?=\s*\n|$)/gi);
  if (sameLineMatch) {
    for (const raw of sameLineMatch) {
      const m = raw.match(/(?:ship\s*to|customer|recipient|delivery\s*to|send\s*to|shipping\s*address)[:\s\^]*([a-zäöüß\s.\-']{2,60})/i);
      if (m && m[1]) {
        for (const name of cleanNameCandidates(m[1])) {
          if (looksLikeName(name, true)) {
            result.customerName = name;
            break;
          }
        }
        if (result.customerName) break;
      }
    }
  }
  if (!result.customerName) {
    const triggerRegex = /ship\s*to|customer|recipient|delivery\s*to|send\s*to|shipping\s*address/i;
    for (let idx = 0; idx < lines.length; idx++) {
      if (!triggerRegex.test(lines[idx])) continue;
      for (let i = idx + 1; i < Math.min(idx + 5, lines.length); i++) {
        const line = lines[i].replace(/\s+/g, ' ').trim();
        const words = line.split(/\s+/).filter(Boolean);
        if (words.length >= 2) {
          const last2 = words.slice(-2).join(' ');
          if (!isRejectedAsName(last2) && looksLikeName(last2, false)) { result.customerName = last2; break; }
        }
        if (words.length >= 3) {
          const last3 = words.slice(-3).join(' ');
          if (!isRejectedAsName(last3) && looksLikeName(last3, true)) { result.customerName = last3; break; }
        }
        if (/\d{4,}|@|\.com|straße|street|road|bldg|〒|copy\s*address/i.test(line)) break;
        for (const candidate of cleanNameCandidates(line)) {
          if (!isRejectedAsName(candidate) && looksLikeName(candidate, true)) {
            result.customerName = candidate;
            break;
          }
        }
        if (result.customerName) break;
      }
      if (result.customerName) break;
    }
  }
  const firstLineStopWords = /^(ship|ordered|quantity|standard|copy|us\$|timetochange|handmade|rainbow|flower|18k|feb|sold|renew|listing|vols|kiewelry)$/i;
  if (!result.customerName && lines.length >= 1) {
    const firstLine = lines[0].replace(/\s+/g, ' ').trim();
    const tokens = firstLine.split(/\s+/);
    const nameWords = [];
    for (const w of tokens) {
      const clean = w.replace(/[»\^▾▼◆●\s]/g, '').trim();
      if (!clean) continue;
      if (clean.length >= 2 && /^[A-Za-zäöüß.\-']+$/.test(clean) && !firstLineStopWords.test(clean)) {
        nameWords.push(clean);
        if (nameWords.length >= 5) break;
      } else break;
    }
    if (nameWords.length >= 1 && nameWords.length <= 5) {
      const fullName = nameWords.join(' ');
      if (!isRejectedAsName(fullName) && looksLikeName(fullName, true)) result.customerName = fullName;
    }
    if (!result.customerName) {
      for (const candidate of cleanNameCandidates(lines[0])) {
        if (!isRejectedAsName(candidate) && looksLikeName(candidate, true)) {
          result.customerName = candidate;
          break;
        }
      }
    }
  }
  const rejectPhrases = /^(ship\s*by|order\s*date|ordered|feb\s*\d|standard\s*shipping|copy\s*address|ring\s*size|gold\s*ruby|diamond\s*ring|maebashi\s*city|kawa\s*bldg|birthstone|jewelry|handmade|bezel|sumiyoshi|timetochange|quantity|sku\s*[a-z])/i;
  if (!result.customerName) {
    for (let i = 0; i < Math.min(8, lines.length); i++) {
      const line = lines[i];
      const twoWord = line.match(/\b([A-Za-zäöüß][A-Za-zäöüß.\-']+)\s+([A-Za-zäöüß][A-Za-zäöüß.\-']+)\b/g);
      if (twoWord) {
        for (const phrase of twoWord) {
          const p = phrase.replace(/\s+/g, ' ').trim();
          if (p.length < 4 || rejectPhrases.test(p)) continue;
          if (!isRejectedAsName(p) && looksLikeName(p, false)) {
            result.customerName = p;
            break;
          }
        }
      }
      if (result.customerName) break;
      const threeWord = line.match(/\b([A-Za-zäöüß][A-Za-zäöüß.\-']+)\s+([A-Za-zäöüß][A-Za-zäöüß.\-']+)\s+([A-Za-zäöüß][A-Za-zäöüß.\-']+)\b/g);
      if (threeWord) {
        for (const phrase of threeWord) {
          const p = phrase.replace(/\s+/g, ' ').trim();
          if (p.length < 4 || rejectPhrases.test(p)) continue;
          if (!isRejectedAsName(p) && looksLikeName(p, true)) {
            result.customerName = p;
            break;
          }
        }
      }
      if (result.customerName) break;
    }
  }
  if (!result.customerName && lines.length >= 2) {
    for (let i = 0; i < Math.min(25, lines.length); i++) {
      const line = lines[i].replace(/\s+/g, ' ').trim();
      if (isRejectedAsName(line)) continue;
      if (/^\d|#|order\s|ship\s|date|total|us\$|€|quantity|sku|price|standard\s*shipping/i.test(line)) continue;
      for (const candidate of cleanNameCandidates(line)) {
        const words = candidate.split(/\s+/).filter(Boolean);
        if (words.length >= 1 && words.length <= 4 && looksLikeName(candidate, true)) {
          result.customerName = candidate;
          break;
        }
      }
      if (result.customerName) break;
    }
  }
  if (result.customerName) result.customerName = result.customerName.replace(/\s+/g, ' ').trim();

  if (/\banirudh\b/i.test(full) && /\betsy\b/i.test(full)) result.sourceOfOrder = 'Anirudh Etsy';
  else if (/\betsy\b/i.test(full)) result.sourceOfOrder = 'K Etsy';
  else if (/\bonline\s*store\b/i.test(full)) result.sourceOfOrder = 'Website';
  else if (/\bwebsite\b/i.test(full)) result.sourceOfOrder = 'Website';
  else if (/\banirudh\b/i.test(full)) result.sourceOfOrder = 'Anirudh Etsy';
  else result.sourceOfOrder = 'Others';

  // Mode of parcel: skipped (not extracted from image)

  return result;
}

export async function extractOrderFromImages(imageDataUrls) {
  const list = Array.isArray(imageDataUrls) ? imageDataUrls : [];
  if (list.length === 0) return null;

  let bestScore = 0;
  let bestText = '';

  for (let i = 0; i < list.length; i++) {
    const dataUrl = list[i];
    if (!dataUrl || typeof dataUrl !== 'string') continue;
    const { data: { text } } = await Tesseract.recognize(dataUrl, 'eng');
    const score = scoreOrderDetailsLikelihood(text);
    if (score > bestScore) {
      bestScore = score;
      bestText = text;
    }
  }

  if (bestScore < 2) return null;
  return parseOrderFields(bestText);
}
